import React, { useState, useEffect } from 'react';
import { eventsAPI } from '../api';
import { Plus, Edit2, Trash2, Upload, Download, Clock, Calendar } from 'lucide-react';
import './Activities.css';

function Activities({ eventId, theme, onActivityClick }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [bulkActivities, setBulkActivities] = useState([{ name: '', description: '', startTime: '', endTime: '' }]);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [saving, setSaving] = useState(false);
  
  // CSV state
  const [csvFile, setCSVFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    fetchActivities();
  }, [eventId]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await eventsAPI.getActivities(eventId);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
      alert('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      alert('Activity name is required');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        start_time: startTime ? new Date(startTime).getTime() : null,
        end_time: endTime ? new Date(endTime).getTime() : null
      };

      await eventsAPI.createActivity(eventId, payload);
      await fetchActivities();
      resetForm();
      setShowCreateModal(false);
      alert('Activity created successfully!');
    } catch (error) {
      console.error('Error creating activity:', error);
      alert('Failed to create activity');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!name.trim()) {
      alert('Activity name is required');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        start_time: startTime ? new Date(startTime).getTime() : null,
        end_time: endTime ? new Date(endTime).getTime() : null
      };

      await eventsAPI.updateActivity(eventId, editingActivity.id, payload);
      await fetchActivities();
      resetForm();
      setShowEditModal(false);
      setEditingActivity(null);
      alert('Activity updated successfully!');
    } catch (error) {
      console.error('Error updating activity:', error);
      alert('Failed to update activity');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (activityId, activityName) => {
    if (!window.confirm(`Are you sure you want to delete "${activityName}"? Photos will not be deleted.`)) {
      return;
    }

    try {
      await eventsAPI.deleteActivity(eventId, activityId);
      await fetchActivities();
      alert('Activity deleted successfully!');
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Failed to delete activity');
    }
  };

  const handleCSVUpload = async () => {
    if (!csvFile) {
      alert('Please select a CSV file');
      return;
    }

    try {
      setUploading(true);
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        const csvData = e.target.result;
        
        try {
          const response = await eventsAPI.uploadActivitiesCSV(eventId, { csvData });
          setUploadResult(response.data);
          await fetchActivities();
          alert(`Successfully created ${response.data.created} activities!`);
          setShowCSVModal(false);
          setCSVFile(null);
        } catch (error) {
          console.error('Error uploading CSV:', error);
          alert(error.response?.data?.error || 'Failed to upload CSV');
        } finally {
          setUploading(false);
        }
      };

      reader.readAsText(csvFile);
    } catch (error) {
      console.error('Error reading CSV file:', error);
      alert('Failed to read CSV file');
      setUploading(false);
    }
  };

  const openEditModal = (activity) => {
    setEditingActivity(activity);
    setName(activity.name);
    setDescription(activity.description || '');
    setStartTime(activity.start_time ? new Date(parseInt(activity.start_time)).toISOString().slice(0, 16) : '');
    setEndTime(activity.end_time ? new Date(parseInt(activity.end_time)).toISOString().slice(0, 16) : '');
    setShowEditModal(true);
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setStartTime('');
    setEndTime('');
  };

  const addBulkRow = () => {
    setBulkActivities([...bulkActivities, { name: '', description: '', startTime: '', endTime: '' }]);
  };

  const removeBulkRow = (index) => {
    setBulkActivities(bulkActivities.filter((_, i) => i !== index));
  };

  const updateBulkRow = (index, field, value) => {
    const updated = [...bulkActivities];
    updated[index][field] = value;
    setBulkActivities(updated);
  };

  const handleBulkCreate = async () => {
    const validActivities = bulkActivities.filter(a => a.name.trim());
    
    if (validActivities.length === 0) {
      alert('Please add at least one activity with a name');
      return;
    }

    try {
      setSaving(true);
      
      for (const activity of validActivities) {
        const payload = {
          name: activity.name.trim(),
          description: activity.description.trim() || null,
          start_time: activity.startTime ? new Date(activity.startTime).getTime() : null,
          end_time: activity.endTime ? new Date(activity.endTime).getTime() : null
        };
        await eventsAPI.createActivity(eventId, payload);
      }
      
      await fetchActivities();
      setBulkActivities([{ name: '', description: '', startTime: '', endTime: '' }]);
      setShowBulkCreateModal(false);
      alert(`Successfully created ${validActivities.length} activities!`);
    } catch (error) {
      console.error('Error creating activities:', error);
      alert('Failed to create some activities');
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplate = () => {
    const template = `Activity Name,Start Time,End Time,Description
Game 1: Team A vs Team B,2024-11-15 08:00,2024-11-15 09:30,Field 1
Game 2: Team C vs Team D,2024-11-15 09:45,2024-11-15 11:15,Field 2
Lunch Break,2024-11-15 11:30,2024-11-15 12:30,Main Pavilion`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'activities_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Not set';
    return new Date(parseInt(timestamp)).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="spinner"></div>
        <p style={{ marginTop: '16px', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
          Loading activities...
        </p>
      </div>
    );
  }

  return (
    <div className="activities-container">
      {/* Header */}
      <div className="activities-header">
        <div>
          <h2 style={{ margin: 0, color: theme === 'dark' ? 'white' : '#111827' }}>
            Event Activities
          </h2>
          <p style={{ margin: '8px 0 0 0', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
            Organize photos by scheduled activities and sessions
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowBulkCreateModal(true)}
            className="btn-secondary"
            style={{ backgroundColor: '#8B5CF6', color: 'white', border: 'none' }}
          >
            <Plus size={16} />
            Bulk Create
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            <Plus size={16} />
            Create Activity
          </button>
        </div>
      </div>

      {/* Activities List */}
      {activities.length === 0 ? (
        <div className="empty-state">
          <Calendar size={48} style={{ color: theme === 'dark' ? '#4b5563' : '#d1d5db', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px 0', color: theme === 'dark' ? 'white' : '#111827' }}>
            No activities yet
          </h3>
          <p style={{ margin: 0, color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
            Create activities to organize photos by scheduled sessions
          </p>
        </div>
      ) : (
        <div className="activities-list">
          {activities.map((activity) => (
            <div 
              key={activity.id} 
              className="activity-card" 
              onClick={() => onActivityClick && onActivityClick(activity.id)}
              style={{ cursor: onActivityClick ? 'pointer' : 'default' }}
            >
              <div className="activity-info">
                <h3 style={{ margin: '0 0 8px 0', color: theme === 'dark' ? 'white' : '#111827' }}>
                  {activity.name}
                </h3>
                {activity.description && (
                  <p style={{ margin: '0 0 12px 0', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                    {activity.description}
                  </p>
                )}
                <div className="activity-times">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Clock size={14} style={{ color: theme === 'dark' ? '#6b7280' : '#9ca3af' }} />
                    <span style={{ fontSize: '14px', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                      {formatDateTime(activity.start_time)}
                    </span>
                  </div>
                  {activity.end_time && (
                    <>
                      <span style={{ color: theme === 'dark' ? '#4b5563' : '#d1d5db' }}>→</span>
                      <span style={{ fontSize: '14px', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                        {formatDateTime(activity.end_time)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="activity-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditModal(activity);
                  }}
                  className="btn-icon"
                  title="Edit activity"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(activity.id, activity.name);
                  }}
                  className="btn-icon btn-danger"
                  title="Delete activity"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Activity Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 24px 0', color: theme === 'dark' ? 'white' : '#111827' }}>
              Create Activity
            </h2>
            
            <div className="form-group">
              <label>Activity Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Game 1: Team A vs Team B"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional details about this activity"
                className="form-input"
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="datetime-local"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>End Time</label>
                <input
                  type="datetime-local"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="btn-primary"
                disabled={saving || !name.trim()}
              >
                {saving ? 'Creating...' : 'Create Activity'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Activity Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 24px 0', color: theme === 'dark' ? 'white' : '#111827' }}>
              Edit Activity
            </h2>
            
            <div className="form-group">
              <label>Activity Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Game 1: Team A vs Team B"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional details about this activity"
                className="form-input"
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="datetime-local"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>End Time</label>
                <input
                  type="datetime-local"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingActivity(null);
                  resetForm();
                }}
                className="btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="btn-primary"
                disabled={saving || !name.trim()}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Create Modal */}
      {showBulkCreateModal && (
        <div className="modal-overlay" onClick={() => setShowBulkCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '80vh', overflow: 'auto' }}>
            <h2 style={{ margin: '0 0 24px 0', color: theme === 'dark' ? 'white' : '#111827' }}>
              Bulk Create Activities
            </h2>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid ' + (theme === 'dark' ? '#374151' : '#e5e7eb') }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontWeight: 600 }}>Name *</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontWeight: 600 }}>Description</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontWeight: 600 }}>Start Time</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontWeight: 600 }}>End Time</th>
                    <th style={{ padding: '12px', width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {bulkActivities.map((activity, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid ' + (theme === 'dark' ? '#374151' : '#e5e7eb') }}>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="text"
                          value={activity.name}
                          onChange={(e) => updateBulkRow(index, 'name', e.target.value)}
                          placeholder="Activity name"
                          className="form-input"
                          style={{ width: '100%', minWidth: '150px' }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="text"
                          value={activity.description}
                          onChange={(e) => updateBulkRow(index, 'description', e.target.value)}
                          placeholder="Optional"
                          className="form-input"
                          style={{ width: '100%', minWidth: '150px' }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="datetime-local"
                          value={activity.startTime}
                          onChange={(e) => updateBulkRow(index, 'startTime', e.target.value)}
                          className="form-input"
                          style={{ width: '100%', minWidth: '180px' }}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="datetime-local"
                          value={activity.endTime}
                          onChange={(e) => updateBulkRow(index, 'endTime', e.target.value)}
                          className="form-input"
                          style={{ width: '100%', minWidth: '180px' }}
                        />
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {bulkActivities.length > 1 && (
                          <button
                            onClick={() => removeBulkRow(index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#ef4444',
                              cursor: 'pointer',
                              padding: '4px',
                              fontSize: '18px'
                            }}
                            title="Remove row"
                          >
                            ×
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={addBulkRow}
              className="btn-secondary"
              style={{ marginBottom: '24px' }}
            >
              <Plus size={16} />
              Add Row
            </button>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowBulkCreateModal(false);
                  setBulkActivities([{ name: '', description: '', startTime: '', endTime: '' }]);
                }}
                className="btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkCreate}
                className="btn-primary"
                disabled={saving || bulkActivities.every(a => !a.name.trim())}
              >
                {saving ? 'Creating...' : `Create ${bulkActivities.filter(a => a.name.trim()).length} Activities`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCSVModal && (
        <div className="modal-overlay" onClick={() => setShowCSVModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 24px 0', color: theme === 'dark' ? 'white' : '#111827' }}>
              Upload Activities CSV
            </h2>
            
            <div className="csv-instructions">
              <p style={{ margin: '0 0 12px 0', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                Upload a CSV file with the following format:
              </p>
              <pre style={{
                background: theme === 'dark' ? '#111827' : '#f3f4f6',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '13px',
                overflow: 'auto',
                color: theme === 'dark' ? '#e5e7eb' : '#374151'
              }}>
Activity Name,Start Time,End Time,Description{'\n'}
Game 1,2024-11-15 08:00,2024-11-15 09:30,Field 1
              </pre>
              <button
                onClick={downloadTemplate}
                className="btn-secondary"
                style={{ marginTop: '12px' }}
              >
                <Download size={16} />
                Download Template
              </button>
            </div>

            <div className="form-group" style={{ marginTop: '24px' }}>
              <label>Select CSV File</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCSVFile(e.target.files[0])}
                className="form-input"
              />
            </div>

            {csvFile && (
              <p style={{ margin: '12px 0 0 0', fontSize: '14px', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
                Selected: {csvFile.name}
              </p>
            )}

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowCSVModal(false);
                  setCSVFile(null);
                }}
                className="btn-secondary"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handleCSVUpload}
                className="btn-primary"
                disabled={uploading || !csvFile}
              >
                {uploading ? 'Uploading...' : 'Upload CSV'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Activities;
